import { useState } from "react";
import { useTranslation } from "react-i18next";

interface Project {
  title: string;
  description: string | string[];
  bigImagesList: string[];
  smallImages: string[];
}

interface ProjectCardProps {
  project: Project;
}

const ProjectCard = ({ project }: ProjectCardProps) => {
  const { title, description, bigImagesList, smallImages } = project;

  // Normalize description to array format
  const descriptionArray = Array.isArray(description)
    ? description
    : [description];

  const [currentIndex, setCurrentIndex] = useState(1); // center is default

  const goLeft = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? bigImagesList.length - 1 : prev - 1
    );
  };

  const goRight = () => {
    setCurrentIndex((prev) =>
      prev === bigImagesList.length - 1 ? 0 : prev + 1
    );
  };

  // for small gallery in lg: always show 3
  const getThreeImages = () => {
    const len = smallImages.length;
    const left = (currentIndex - 1 + len) % len;
    const center = currentIndex;
    const right = (currentIndex + 1) % len;
    return [left, center, right];
  };

  const three = getThreeImages();

  // for mobile: four-cell layout - sliding window based on currentIndex
  const getFourCells = () => {
    const len = smallImages.length;
    const cells: (string | null)[] = [];
    
    // Show 4 images starting from currentIndex, wrapping around if needed
    for (let i = 0; i < 4; i++) {
      const idx = (currentIndex + i) % len;
      cells.push(smallImages[idx]);
    }
    
    // Fill empty slots if we have fewer than 4 images total
    while (cells.length < 4) {
      cells.push(null);
    }
    
    return cells;
  };

  const fourCells = getFourCells();

  return (
    <div className="bg-white">
      {/* Title + HR - Top of card for all devices */}
      <div className="lg:p-[40px] p-[30px] pb-0 lg:pb-0">
        <div className="font-['Forum'] text-[24px] lg:text-[36px] mb-[20px] font-normal leading-[28px] lg:leading-[48px] text-red-900 uppercase">
          {title}
        </div>
        <hr className="border-red-900 border-t-2" />
      </div>

      <div className="w-full h-full flex flex-col lg:flex-row lg:p-[40px] p-[30px] pt-5 lg:pt-5">
        {/* MOBILE & MD VIEW */}
        <div className="lg:hidden w-full flex flex-col">
          {/* Big image full width + arrows */}
          <div className="mb-5 relative">
            <img
              src={bigImagesList[currentIndex]}
              alt={title}
              className="w-full h-auto object-cover"
            />
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center justify-between px-4">
              <button
                type="button"
                onClick={goLeft}
                className="w-9 h-9 flex items-center justify-center rounded-full text-white text-xl cursor-pointer"
                style={{ backgroundColor: "rgba(136, 31, 31, 0.75)" }}
              >
                ‹
              </button>
              <button
                type="button"
                onClick={goRight}
                className="w-9 h-9 flex items-center justify-center rounded-full text-white text-xl cursor-pointer"
                style={{ backgroundColor: "rgba(136, 31, 31, 0.75)" }}
              >
                ›
              </button>
            </div>
          </div>

          {/* 4 small images grid */}
          <div className="grid grid-cols-4 gap-2 mb-5">
            {fourCells.map((img, i) => {
              if (!img) {
                return <div key={i} className="w-full aspect-square" />;
              }

              // Calculate the actual index in the smallImages array
              const len = smallImages.length;
              const realIdx = (currentIndex + i) % len;
              const isActive = realIdx === currentIndex;

              return (
                <div key={`${realIdx}-${i}`} className="w-full aspect-square">
                  <img
                    src={img}
                    alt={`${title} - ${realIdx + 1}`}
                    className="w-full h-full object-cover cursor-pointer"
                    style={{
                      filter: isActive ? "none" : "blur(1px)",
                      opacity: isActive ? 1 : 0.9,
                    }}
                    onClick={() => setCurrentIndex(realIdx)}
                  />
                </div>
              );
            })}
          </div>

          {/* Description */}
          <div className="font-['Inter'] text-[14px] font-light leading-[20px] text-red-900 whitespace-normal break-words">
            {descriptionArray.map((para, idx) => (
              <p key={idx} className={idx > 0 ? "mt-4" : ""}>
                {para}
              </p>
            ))}
          </div>
        </div>

        {/* DESKTOP (≥ lg) */}
        <div className="hidden lg:flex lg:flex-row lg:items-start w-full gap-6">
          {/* Left: Big Image Column + arrows */}
          <div className="lg:w-1/2 lg:pr-[20px] relative">
            <img
              src={bigImagesList[currentIndex]}
              alt={title}
              className="w-full h-[540px] object-cover object-center"
            />
            <button
              type="button"
              onClick={goLeft}
              className="hidden lg:flex absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full text-white text-2xl cursor-pointer"
              style={{ backgroundColor: "rgba(136, 31, 31, 0.75)" }}
            >
              ‹
            </button>
            <button
              type="button"
              onClick={goRight}
              className="hidden lg:flex absolute right-[35px] top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full text-white text-2xl cursor-pointer"
              style={{ backgroundColor: "rgba(136, 31, 31, 0.75)" }}
            >
              ›
            </button>
          </div>

          {/* Right: Small Images and Text Column */}
          <div className="flex flex-col flex-grow lg:w-1/2">
            {/* Row 1: Three small images */}
            <div className="flex items-center justify-center gap-4 mb-6">
              {three.map((idx, pos) => {
                const isCenter = pos === 1;
                return (
                  <div
                    key={idx}
                    onClick={() => {
                      if (!isCenter) {
                        if (pos === 0) goLeft();
                        else if (pos === 2) goRight();
                      }
                    }}
                    className={`cursor-pointer transition-all duration-300 ${
                      isCenter ? "opacity-100" : "opacity-90"
                    }`}
                    style={{
                      width: "192px",
                      height: "108px",
                      ...(isCenter ? {} : { filter: "blur(1px)" }),
                    }}
                  >
                    <img
                      src={smallImages[idx]}
                      alt={`${title} - ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                );
              })}
            </div>

            {/* Row 2: Description */}
            <div className="flex flex-col flex-grow lg:p-[40px] lg:pr-[40px]">
              <div className="font-['Inter'] text-[14px] font-light leading-[20px] text-red-900 whitespace-normal break-words">
                {descriptionArray.map((para, idx) => (
                  <p key={idx} className={idx > 0 ? "mt-4" : ""}>
                    {para}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProjectsWhiteSection = () => {
  const { t } = useTranslation();
  const projectsDataRaw = t("projects", { returnObjects: true });
  const projectsData = Array.isArray(projectsDataRaw)
    ? (projectsDataRaw as Array<{
        title: string;
        description: string | string[];
      }>)
    : [];

  // Images remain static (same for all languages)
  const projectsImages = [
    {
      bigImagesList: [
        "yassy/1-min.jpg",
        "yassy/2-min.jpg",
        "yassy/3-min.jpg",
        "yassy/4-min.jpg",
        "yassy/5-min.jpg",
        "yassy/6-min.jpg",
        "yassy/7-min.jpg",
        "yassy/8-min.jpg",
        "yassy/9-min.jpg",
        "yassy/10-min.jpg",
      ],
      smallImages: [
        "yassy/1-min.jpg",
        "yassy/2-min.jpg",
        "yassy/3-min.jpg",
        "yassy/4-min.jpg",
        "yassy/5-min.jpg",
        "yassy/6-min.jpg",
        "yassy/7-min.jpg",
        "yassy/8-min.jpg",
        "yassy/9-min.jpg",
        "yassy/10-min.jpg",
      ],
    },
    {
      bigImagesList: [
        "ps-juldyz/1-min.jpg",
        "ps-juldyz/2-min.jpg",
        "ps-juldyz/3-min.jpg",
        "ps-juldyz/4-min.jpg",
        "ps-juldyz/5-min.jpg",
        "ps-juldyz/6-min.jpg",
        "ps-juldyz/7-min.jpg",
        "ps-juldyz/8-min.jpg",
        "ps-juldyz/9-min.jpg",
      ],
      smallImages: [
        "ps-juldyz/1-min.jpg",
        "ps-juldyz/2-min.jpg",
        "ps-juldyz/3-min.jpg",
        "ps-juldyz/4-min.jpg",
        "ps-juldyz/5-min.jpg",
        "ps-juldyz/6-min.jpg",
        "ps-juldyz/7-min.jpg",
        "ps-juldyz/8-min.jpg",
        "ps-juldyz/9-min.jpg",
      ],
    },
    {
      bigImagesList: [
        "asia-trafo/1-min.jpg",
        "asia-trafo/2-min.jpg",
        "asia-trafo/3-min.jpg",
        "asia-trafo/4-min.jpg",
        "asia-trafo/5-min.jpg",
        "asia-trafo/6.-min.jpg",
        "asia-trafo/7-min.jpg",
        "asia-trafo/8-min.jpg",
        "asia-trafo/9-min.jpg",
        "asia-trafo/10-min.jpg",
        "asia-trafo/11-min.jpg",
        "asia-trafo/12-min.jpg",
        "asia-trafo/13-min.jpg",
        "asia-trafo/14-min.jpg",
      ],
      smallImages: [
        "asia-trafo/1-min.jpg",
        "asia-trafo/2-min.jpg",
        "asia-trafo/3-min.jpg",
        "asia-trafo/4-min.jpg",
        "asia-trafo/5-min.jpg",
        "asia-trafo/6.-min.jpg",
        "asia-trafo/7-min.jpg",
        "asia-trafo/8-min.jpg",
        "asia-trafo/9-min.jpg",
        "asia-trafo/10-min.jpg",
        "asia-trafo/11-min.jpg",
        "asia-trafo/12-min.jpg",
        "asia-trafo/13-min.jpg",
        "asia-trafo/14-min.jpg",
      ],
    },
    {
      bigImagesList: [
        "jk-alageum-electric/1-min.jpg",
        "jk-alageum-electric/2-min.jpg",
        "jk-alageum-electric/3-min.jpg",
        "jk-alageum-electric/4-min.jpg",
        "jk-alageum-electric/5-min.jpg",
        "jk-alageum-electric/6-min.jpg",
        "jk-alageum-electric/7-min.jpg",
        "jk-alageum-electric/8-min.jpg",
        "jk-alageum-electric/9-min.jpg",
        "jk-alageum-electric/10-min.jpg",
        "jk-alageum-electric/11-min.jpg",
        "jk-alageum-electric/12-min.jpg",
        "jk-alageum-electric/13-min.jpg",
        "jk-alageum-electric/14-min.jpg",
        "jk-alageum-electric/15-min.jpg",
      ],
      smallImages: [
        "jk-alageum-electric/1-min.jpg",
        "jk-alageum-electric/2-min.jpg",
        "jk-alageum-electric/3-min.jpg",
        "jk-alageum-electric/4-min.jpg",
        "jk-alageum-electric/5-min.jpg",
        "jk-alageum-electric/6-min.jpg",
        "jk-alageum-electric/7-min.jpg",
        "jk-alageum-electric/8-min.jpg",
        "jk-alageum-electric/9-min.jpg",
        "jk-alageum-electric/10-min.jpg",
        "jk-alageum-electric/11-min.jpg",
        "jk-alageum-electric/12-min.jpg",
        "jk-alageum-electric/13-min.jpg",
        "jk-alageum-electric/14-min.jpg",
        "jk-alageum-electric/15-min.jpg",
      ],
    },
    {
      bigImagesList: [
        "ps-sez/1-min.jpg",
        "ps-sez/2-min.jpg",
        "ps-sez/3-min.jpeg",
        "ps-sez/4-min.jpeg",
        "ps-sez/5-min.jpeg",
        "ps-sez/6-min.jpeg",
        "ps-sez/7-min.jpeg",
        "ps-sez/8-min.jpg",
      ],
      smallImages: [
        "ps-sez/1-min.jpg",
        "ps-sez/2-min.jpg",
        "ps-sez/3-min.jpeg",
        "ps-sez/4-min.jpeg",
        "ps-sez/5-min.jpeg",
        "ps-sez/6-min.jpeg",
        "ps-sez/7-min.jpeg",
        "ps-sez/8-min.jpg",
      ],
    },
    {
      bigImagesList: [
        "ps-aray/1-min.jpg",
        "ps-aray/2-min.jpeg",
        "ps-aray/3-min.jpeg",
        "ps-aray/4-min.jpg",
        "ps-aray/5-min.jpg",
        "ps-aray/6-min.jpg",
        "ps-aray/7-min.jpg",
        "ps-aray/8-min.jpeg",
        "ps-aray/9-min.jpeg",
        "ps-aray/10-min.jpeg",
      ],
      smallImages: [
        "ps-aray/1-min.jpg",
        "ps-aray/2-min.jpeg",
        "ps-aray/3-min.jpeg",
        "ps-aray/4-min.jpg",
        "ps-aray/5-min.jpg",
        "ps-aray/6-min.jpg",
        "ps-aray/7-min.jpg",
        "ps-aray/8-min.jpeg",
        "ps-aray/9-min.jpeg",
        "ps-aray/10-min.jpeg",
      ],
    },
  ];

  // Combine translated data with images
  const projects = projectsData.map((project, index) => ({
    ...project,
    ...projectsImages[index],
  }));

  return (
    <section id="projects" className="w-full">
      <div
        className="bg-white flex h-[185px] items-center justify-start pl-10 w-full text-[#851919] font-['Forum'] text-[48px] font-normal uppercase leading-[40px] md:leading-[66px]"
        style={{
          leadingTrim: "both",
          textEdge: "cap",
        } as React.CSSProperties & { leadingTrim?: string; textEdge?: string }}
      >
        {t("projectsSection.title")}
      </div>

      {projects.map((project, projectIndex) => (
        <ProjectCard key={projectIndex} project={project} />
      ))}
    </section>
  );
};

export default ProjectsWhiteSection;
